import { type ReactNode, useEffect } from 'react';

import { Modal } from '@/design-system';
import { spacing } from '@/design-system/spacing';
import {
  useWalletSendSuggestion,
  type WalletSendSuggestion,
} from '@/features/wallet-access/use-wallet-send-suggestion';
import { useWalletAccess } from '@/features/wallet-access/wallet-access-provider';
import type { WalletMfaVerificationProps } from '@/features/wallet-mfa/wallet-mfa-verification.types';
import { WalletMfaVerificationInline } from '@/features/wallet-mfa/wallet-mfa-verification-dialog';
import { useInlineWalletMfaVerification } from '@/features/wallet-mfa/wallet-mfa-verification-provider';
import { WalletTotpSetupDialog } from '@/pages/settings/modules/personal-info/wallet-mfa/wallet-totp-setup-dialog';
import { CryptoAsset } from '@/shared/modules/wallet/use-crypto-assets';

import { SendActions, type SendActionsProps } from './send-actions';
import { sendFlowActions } from './send-flow-actions';
import { SendFlowBody } from './send-flow-body';
import {
  resolveSendFlowView,
  resolveSendPageLayout,
  SendFlowView,
  SendPageLayout,
} from './send-flow-view';
import {
  SendActivityBoundary,
  SendPageFlow,
  SendPageStatusFlow,
  SendPageStatusViewport,
  SendPageVerificationFlow,
} from './send-page-layout';
import { type SendFlowViewModel, useSendFlow } from './use-send-flow';

const MODAL_WIDTH = 600;
const MODAL_GAP = 16;
const MODAL_MAX_HEIGHT = `calc(100dvh - ${spacing[32]}px)`;

type SendPresentation = 'modal' | 'page';

export interface SendFlowProps {
  presentation: SendPresentation;
  open: boolean;
  onClose: () => void;
  assets: CryptoAsset[];
  senderAddress: string | undefined;
  initialCoin?: CryptoAsset;
  onPageProcessingChange?: (_processing: boolean) => void;
}

interface SendFlowFrame {
  actions: SendActionsProps;
  enrollment: ReactNode;
  onActivity: () => void;
}

type SendFlowFrameProps = SendFlowFrame & { children: ReactNode };

export const SendFlow = (props: SendFlowProps) => {
  const { presentation, open, assets, onPageProcessingChange } = props;
  const walletAccess = useWalletAccess();
  const suggestion = useWalletSendSuggestion(open);
  const verification = useInlineWalletMfaVerification(open);
  const flow = useSendFlow(props);
  const view = resolveSendFlowView({
    verificationOpen: verification?.open === true,
    suggestionOpen: suggestion.open,
    isUnlocked: walletAccess.isUnlocked,
    flow,
  });
  const pageProcessing = flow.isBusy && view !== SendFlowView.Verification;
  useReportPageProcessing(presentation, pageProcessing, onPageProcessingChange);

  const closeModal = () => {
    suggestion.cancel();

    if (verification && view === SendFlowView.Verification) {
      cancelSendVerification(flow, verification);
    }

    flow.handleClose();
  };

  const frame: SendFlowFrame = {
    actions: sendFlowActions(view, {
      presentation,
      flow,
      suggestion,
      walletBusy: walletAccess.isBusy,
    }),
    enrollment: <SuggestedTotpEnrollment suggestion={suggestion} />,
    onActivity: walletAccess.recordActivity,
  };
  const body = (
    <SendFlowBody
      view={view}
      flow={flow}
      assets={assets}
      verification={
        <SendVerification verification={verification} presentation={presentation} flow={flow} />
      }
    />
  );

  if (presentation === 'modal') {
    return (
      <SendFlowModal {...frame} open={open && !suggestion.enrolling} onClose={closeModal}>
        {body}
      </SendFlowModal>
    );
  }

  return (
    <SendFlowPage {...frame} layout={resolveSendPageLayout(view, flow)}>
      {body}
    </SendFlowPage>
  );
};

const SuggestedTotpEnrollment = ({ suggestion }: { suggestion: WalletSendSuggestion }) => (
  <WalletTotpSetupDialog
    open={suggestion.enrolling}
    management={suggestion.management}
    onClose={suggestion.closeEnrollment}
  />
);

const cancelSendVerification = (
  flow: SendFlowViewModel,
  verification: WalletMfaVerificationProps,
) => {
  flow.handleVerificationCancel();
  verification.onCancel();
};

const SendVerification = ({
  verification,
  presentation,
  flow,
}: {
  verification: WalletMfaVerificationProps | null;
  presentation: SendPresentation;
  flow: SendFlowViewModel;
}) => {
  if (!verification) {
    return null;
  }

  const restart = () => {
    verification.onRestart();
    flow.handleRetry();
  };

  return (
    <WalletMfaVerificationInline
      {...verification}
      fillAvailableHeight={presentation === 'page'}
      onRestart={restart}
      onCancel={() => cancelSendVerification(flow, verification)}
    />
  );
};

const SendFlowModal = ({
  open,
  onClose,
  actions,
  enrollment,
  onActivity,
  children,
}: SendFlowFrameProps & { open: boolean; onClose: () => void }) => (
  <>
    <Modal
      open={open}
      onOpenChange={(next, { reason }) => {
        if (!next && reason !== 'outside-press') {
          onClose();
        }
      }}
      title="Send"
      maxWidth={MODAL_WIDTH}
      maxHeight={MODAL_MAX_HEIGHT}
      gap={MODAL_GAP}
      contentPadding={0}
      {...actions}
    >
      <SendActivityBoundary onPointerDownCapture={onActivity} onKeyDownCapture={onActivity}>
        {children}
      </SendActivityBoundary>
    </Modal>
    {enrollment}
  </>
);

const SendFlowPage = ({
  layout,
  actions,
  enrollment,
  onActivity,
  children,
}: SendFlowFrameProps & { layout: SendPageLayout }) => {
  if (layout === SendPageLayout.Verification) {
    return (
      <SendPageVerificationFlow aria-label="Send wallet verification">
        {children}
      </SendPageVerificationFlow>
    );
  }

  if (layout === SendPageLayout.Status) {
    return (
      <SendPageStatusFlow aria-label="Send transaction status">
        <SendPageStatusViewport>{children}</SendPageStatusViewport>
        <SendActions {...actions} />
      </SendPageStatusFlow>
    );
  }

  return (
    <SendPageFlow
      aria-label="Send funds"
      onPointerDownCapture={onActivity}
      onKeyDownCapture={onActivity}
    >
      {children}
      <SendActions {...actions} />
      {enrollment}
    </SendPageFlow>
  );
};

function useReportPageProcessing(
  presentation: SendPresentation,
  processing: boolean,
  onChange: ((processing: boolean) => void) | undefined,
): void {
  useEffect(() => {
    if (presentation !== 'page' || !onChange) {
      return;
    }

    onChange(processing);

    return () => {
      if (processing) {
        onChange(false);
      }
    };
  }, [onChange, processing, presentation]);
}
